/**
 * Route guard that ensures user has an active organization
 * Redirects to onboarding if no org exists
 */

import { useEffect, useState } from 'react'
import { Alert, Box, Button } from '@mui/material'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useOrgStore } from '../../store/orgStore'
import { organizationApi } from '../../services/organizationApi'
import { ArcherLoader } from '../../design-system/components'

export function RequireOrg() {
  const location = useLocation()
  const { activeOrg, orgs: knownOrgs, setActiveOrg, setOrgs, clearOrg } = useOrgStore()
  const [loading, setLoading] = useState(true)
  const [hasOrg, setHasOrg] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [readAttempt, setReadAttempt] = useState(0)

  useEffect(() => {
    let mounted = true

    const checkOrganization = async () => {
      setLoading(true)
      setError(null)
      try {
        // The persisted selection does not contain the organization directory.
        // Reuse a loaded directory, or hydrate it through this existing owner.
        const knownOrganization = knownOrgs.find(org => org.id === activeOrg?.id)
        if (knownOrganization) {
          if (knownOrganization !== activeOrg) setActiveOrg(knownOrganization)
          console.log('✅ Active org exists:', knownOrganization.name)
          setHasOrg(true)
          setLoading(false)
          return
        }

        console.log('🔍 Loading authorized organization choices...')

        // Fetch user's organizations
        const orgs = await organizationApi.listMyOrgs()
        console.log('📊 Found', orgs.length, 'organizations')

        if (!mounted) return

        setOrgs(orgs)

        if (orgs.length === 0) {
          clearOrg()
          console.log('❌ No organizations found - redirecting to onboarding')
          setHasOrg(false)
          setLoading(false)
          return
        }

        // Preserve a remembered selection only when the current read confirms it.
        const selectedOrg = orgs.find(org => org.id === activeOrg?.id) ?? orgs[0]
        console.log('✅ Setting active org:', selectedOrg.name)
        setActiveOrg(selectedOrg)
        setHasOrg(true)
        setLoading(false)
      } catch (error) {
        console.error('❌ Error checking organizations:', error)
        if (!mounted) return
        setError('Your organizations could not be loaded. Retry to continue.')
        setLoading(false)
      }
    }

    checkOrganization()

    return () => {
      mounted = false
    }
  }, [activeOrg, knownOrgs, setActiveOrg, setOrgs, clearOrg, readAttempt])

  // Show loader while checking
  if (loading) {
    return (
      <ArcherLoader
        fullscreen
        size={200}
        message="Checking organization..."
      />
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" onClick={() => setReadAttempt(attempt => attempt + 1)}>Retry</Button>
        }>
          {error}
        </Alert>
      </Box>
    )
  }

  // Redirect to onboarding only after a successful empty read.
  if (!hasOrg) {
    console.log('🔄 Redirecting to onboarding (no org found)')
    return <Navigate to="/onboarding" state={{ from: location }} replace />
  }

  // Render protected route
  return <Outlet />
}
